<script setup lang="ts">
import type { SubmitEventPromise } from 'vuetify';
import type {
  InitialData as SavingCurrentSearchDialogFormInitialData,
  ValidData as SavingCurrentSearchDialogFormValidData,
} from '@/components/dialogForms/SavingCurrentSearchDialogForm.vue';

import { computed, useTemplateRef } from 'vue';

import KBtn from '@/components/btns/KBtn.vue';
import SavingCurrentSearchDialogForm from '@/components/dialogForms/SavingCurrentSearchDialogForm.vue';
import ReportGenerateDialogHeader from '@/components/headers/ReportGenerateDialogHeader.vue';
import PageDialogWrapper from '@/components/wrappers/PageDialogWrapper.vue';

export interface Props {
  isConfirmLoading?: boolean;
  confirm?: (
    formData: SavingCurrentSearchDialogFormValidData,
  ) => Promise<unknown>;
}
export type DefProps = typeof props;
const props = withDefaults(defineProps<Props>(), {
  isConfirmLoading: false,
  confirm: undefined,
});
const emit = defineEmits<{
  cancelBtnClick: [event: Event];
  confirmBtnClick: [
    event: SubmitEventPromise | null | undefined,
    formData: SavingCurrentSearchDialogFormValidData,
    props: DefProps,
  ];
}>();

function handleCancelClick(event: Event): void {
  // also it should be wrapped with <v-dialog :persistent="isConfirmLoading"
  if (props.isConfirmLoading) return;
  emit('cancelBtnClick', event);
}

function handleSubmit(
  event: SubmitEventPromise | null | undefined,
  formData: SavingCurrentSearchDialogFormValidData,
): void {
  if (props.isConfirmLoading) return;
  emit('confirmBtnClick', event, formData, props);
}

const mainEl = useTemplateRef('main');

function getSubmitBtn(): HTMLInputElement | null {
  if (!mainEl.value) return null;
  return mainEl.value.querySelector('input[type=submit]');
}

function handleConfirmClick(event: Event): void {
  const submitBtn = getSubmitBtn();
  if (!submitBtn) return;
  submitBtn.click();
}

const initialData = computed<SavingCurrentSearchDialogFormInitialData>(() => {
  return {
    title: undefined,
    filterType: undefined,
  };
});
</script>

<template>
  <PageDialogWrapper
    :class="$style.savingCurrentSearchDialog"
    data-testid="savingCurrentSearchDialog"
  >
    <template #header>
      <ReportGenerateDialogHeader @close-click="handleCancelClick">
        Saving Current Search
      </ReportGenerateDialogHeader>
    </template>
    <section ref="main" :class="$style.main">
      <SavingCurrentSearchDialogForm
        :initial-data="initialData"
        :loading="props.isConfirmLoading"
        @submit="handleSubmit"
      />
    </section>
    <template #footer>
      <menu :class="$style.dialogFooterMenuList">
        <li :class="$style.dialogFooterMenuListItem">
          <KBtn
            variant="outlined"
            append-icon="mdi-close"
            :class="$style.btn"
            @click="handleCancelClick"
          >
            Cancel
          </KBtn>
        </li>
        <li :class="$style.dialogFooterMenuListItem">
          <KBtn
            append-icon="mdi-check"
            :class="$style.btn"
            :loading="props.isConfirmLoading"
            :disabled="!props.confirm"
            @click="handleConfirmClick"
          >
            Save
          </KBtn>
        </li>
      </menu>
    </template>
  </PageDialogWrapper>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.savingCurrentSearchDialog {
  line-height: 1.4;
  width: variables.$reportGenerateDialogWidth;
}
  .main {
    margin-bottom: -16px;
  }
  .dialogFooterMenuList {
    list-style: none;
    margin: 0;
    padding: 0;
    text-align: right;
  }
    .dialogFooterMenuListItem {
      display: inline;
    }
    .dialogFooterMenuListItem + .dialogFooterMenuListItem {
      margin-left: 1rem;
    }
      .btn {
      }
</style>
